/**
 * Toast Notification Service
 * Provides non-blocking toast notifications using Bootstrap 5
 *
 * Usage:
 *   const toastService = ToastService.getInstance();
 *   toastService.showError('Something went wrong');
 *   toastService.showSuccess('Document saved');
 *   toastService.showInfo('Processing...');
 *   toastService.showWarning('Connection unstable');
 */

import Toast from 'bootstrap/js/src/toast.js';
import { escapeHtml } from '../core/utilities/dom';

export type ToastType = 'error' | 'success' | 'info' | 'warning';

export interface ToastOptions {
  /** Auto-dismiss delay in ms (0 = no auto-dismiss). Default varies by type. */
  duration?: number;
  /** Show close button. Default: true */
  dismissible?: boolean;
  /** Optional title above message */
  title?: string;
  /** Optional button after the message; clicking it runs onClick and closes the toast. */
  action?: { label: string; onClick: () => void };
}

/** Default durations by toast type (in milliseconds) */
const DEFAULT_DURATIONS: Record<ToastType, number> = {
  success: 4000,
  info: 6000,
  warning: 8000,
  error: 10000,
};

/** Bootstrap color classes by toast type */
const TYPE_CLASSES: Record<ToastType, string> = {
  error: 'text-bg-danger',
  success: 'text-bg-success',
  info: 'text-bg-info',
  warning: 'text-bg-warning',
};

/** Icons by toast type (using Unicode symbols for simplicity) */
const TYPE_ICONS: Record<ToastType, string> = {
  error: '\u2716', // ✖
  success: '\u2714', // ✔
  info: '\u2139', // ℹ
  warning: '\u26A0', // ⚠
};

/** ARIA live region settings by toast type */
const ARIA_LIVE: Record<ToastType, 'assertive' | 'polite'> = {
  error: 'assertive',
  success: 'polite',
  info: 'polite',
  warning: 'assertive',
};

/**
 * Toast Notification Service
 * Singleton class for displaying Bootstrap toast notifications
 */
class ToastService {
  private static instance: ToastService | null = null;
  private container: HTMLElement | null = null;
  private readonly containerId = 'toast-container';

  private constructor() {
    // Private constructor for singleton pattern
  }

  /**
   * Get the singleton instance of ToastService
   */
  static getInstance(): ToastService {
    if (!ToastService.instance) {
      ToastService.instance = new ToastService();
    }
    return ToastService.instance;
  }

  /**
   * Show an error toast (red, longer duration)
   */
  showError(message: string, options?: ToastOptions): void {
    this.show(message, 'error', options);
  }

  /**
   * Show a success toast (green, shorter duration)
   */
  showSuccess(message: string, options?: ToastOptions): void {
    this.show(message, 'success', options);
  }

  /**
   * Show an info toast (blue, medium duration)
   */
  showInfo(message: string, options?: ToastOptions): void {
    this.show(message, 'info', options);
  }

  /**
   * Show a warning toast (yellow, medium-long duration)
   */
  showWarning(message: string, options?: ToastOptions): void {
    this.show(message, 'warning', options);
  }

  /**
   * Show a toast notification
   */
  private show(message: string, type: ToastType, options?: ToastOptions): void {
    this.ensureContainer();

    const duration = options?.duration ?? DEFAULT_DURATIONS[type];
    const dismissible = options?.dismissible ?? true;
    const title = options?.title;

    const toastElement = this.createToastElement(
      message,
      type,
      title,
      dismissible,
      duration
    );

    if (options?.action) {
      const { label, onClick } = options.action;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'btn btn-sm btn-light ms-2';
      button.dataset.toastAction = '';
      button.textContent = label;
      button.addEventListener('click', () => {
        onClick();
        Toast.getOrCreateInstance(toastElement).hide();
      });
      toastElement.querySelector('.toast-body')?.append(button);
    }

    this.container!.appendChild(toastElement);

    // Initialize and show the Bootstrap toast
    const bsToast = new Toast(toastElement, {
      autohide: duration > 0,
      delay: duration,
    });

    // Remove element from DOM after it's hidden
    toastElement.addEventListener('hidden.bs.toast', () => {
      toastElement.remove();
    });

    bsToast.show();
  }

  /**
   * Create the toast HTML element
   */
  private createToastElement(
    message: string,
    type: ToastType,
    title: string | undefined,
    dismissible: boolean,
    _duration: number
  ): HTMLElement {
    const toast = document.createElement('div');
    toast.className = `toast align-items-center ${TYPE_CLASSES[type]} border-0`;
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', ARIA_LIVE[type]);
    toast.setAttribute('aria-atomic', 'true');

    const icon = TYPE_ICONS[type];

    // Use text-dark for warning toasts (yellow background needs dark text)
    const textClass = type === 'warning' ? 'text-dark' : '';
    const closeButtonClass =
      type === 'warning' ? 'btn-close' : 'btn-close btn-close-white';

    const closeButtonHtml = dismissible
      ? `<button type="button" class="${closeButtonClass} me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>`
      : '';

    if (title) {
      toast.innerHTML = `
        <div class="toast-header ${TYPE_CLASSES[type]} ${textClass}">
          <span class="me-2">${icon}</span>
          <strong class="me-auto">${escapeHtml(title)}</strong>
          ${closeButtonHtml}
        </div>
        <div class="toast-body ${textClass}">
          ${escapeHtml(message)}
        </div>
      `;
    } else {
      toast.innerHTML = `
        <div class="d-flex">
          <div class="toast-body ${textClass}">
            <span class="me-2">${icon}</span>
            ${escapeHtml(message)}
          </div>
          ${closeButtonHtml}
        </div>
      `;
    }

    return toast;
  }

  /**
   * Ensure the toast container exists in the DOM
   */
  private ensureContainer(): void {
    if (this.container && document.body.contains(this.container)) {
      return;
    }

    // Check if container already exists (e.g., from previous instance)
    let existing = document.getElementById(this.containerId);
    if (existing) {
      this.container = existing;
      return;
    }

    // Create new container
    this.container = document.createElement('div');
    this.container.id = this.containerId;
    this.container.className = 'toast-container position-fixed end-0 p-3';
    this.container.style.zIndex = '1090'; // Above modals (1055) but below tooltips (1100)
    this.container.setAttribute('aria-label', 'Notifications');

    document.body.appendChild(this.container);
  }

  /**
   * Remove the toast container from the DOM
   */
  destroy(): void {
    if (this.container && document.body.contains(this.container)) {
      this.container.remove();
    }
    this.container = null;
  }
}

export default ToastService;
